import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IChartEventSetting } from '.';

/** 事件设置默认值（与 useEventMetricTrendKpiEventUi 保持一致） */
export const DEFAULT_EVENT_SETTING: IChartEventSetting = {
  bubbleEvent: {},
  showEventAxis: true,
  showEventBubbles: true,
  showExperimentEventDetailFlat: false,
};

/** 深拷贝事件数据，避免引用污染 */
const cloneEventMap = (m: Record<string, unknown[]> | undefined): Record<string, unknown[]> => {
  if (!m) return {};
  const o: Record<string, unknown[]> = {};
  Object.keys(m).forEach(k => {
    const arr = m[k];
    o[k] = Array.isArray(arr) ? [...arr] : [];
  });
  return o;
};

/** 用日期键 + 条数构建同步 key，避免引用变更导致反复重置 */
const buildEventMapSyncKey = (m: Record<string, unknown[]> | undefined): string => {
  if (!m) return '';
  return JSON.stringify(
    Object.keys(m)
      .sort()
      .map(k => [k, Array.isArray(m[k]) ? m[k].length : 0]),
  );
};

/**
 * KPI 事件设置 Hook
 *
 * 支持受控/非受控双模式：
 * - 受控模式：外部传入 chartEventSetting + changeEventSetting，完全由外部控制
 * - 非受控模式：内部 useState 管理，eventModalData 变化时自动同步 bubbleEvent
 *
 * @example
 * // 非受控（开箱即用）
 * const { eventSetting, changeEventSetting } = useKpiEventSetting({ eventModalData });
 *
 * // 受控（外部控制）
 * const { eventSetting, changeEventSetting } = useKpiEventSetting({
 *   chartEventSetting: myState,
 *   changeEventSetting: setMyState,
 * });
 */
export function useKpiEventSetting(options?: {
  /** 外部受控状态（传入则启用受控模式） */
  chartEventSetting?: IChartEventSetting;
  /** 外部受控变更回调 */
  changeEventSetting?: (setting: IChartEventSetting) => void;
  /** 弹窗数据：按 event_type 分组的事件列表（非受控模式下用于自动同步 bubbleEvent） */
  eventModalData?: Record<string, unknown[]>;
}): {
  /** 当前生效的事件设置 */
    eventSetting: IChartEventSetting;
    /** 变更事件设置的回调 */
    changeEventSetting: (setting: IChartEventSetting) => void;
  } {
  const {
    chartEventSetting,
    changeEventSetting: externalChangeSetting,
    eventModalData,
  } = options || {};

  const isControlledMode = !!chartEventSetting && !!externalChangeSetting;

  // 非受控模式的内部状态
  const [internalEventSetting, setInternalEventSetting] =
    useState<IChartEventSetting>(DEFAULT_EVENT_SETTING);

  // 当前生效值：受控取外部，非受控取内部
  const effectiveEventSetting: IChartEventSetting = isControlledMode
    ? (chartEventSetting as IChartEventSetting)
    : internalEventSetting;

  // eventModalData 变化时同步 bubbleEvent（仅非受控模式）
  const eventSyncKey = useMemo(() => buildEventMapSyncKey(eventModalData), [eventModalData]);

  useEffect(() => {
    if (isControlledMode || !eventModalData) return;
    setInternalEventSetting(prev => ({
      ...prev,
      bubbleEvent: cloneEventMap(eventModalData),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 刻意只用 syncKey；eventModalData 引用不稳
  }, [isControlledMode, eventSyncKey, eventModalData]);

  // 内部变更函数（非受控模式使用）
  // 勾选「显示事件」仅改轴/气泡开关、bubbleEvent 引用不变时跳过 clone，避免大列表下卡顿
  const internalChangeSetting = useCallback((setting: IChartEventSetting) => {
    setInternalEventSetting(prev => ({
      bubbleEvent:
        setting.bubbleEvent === prev.bubbleEvent
          ? prev.bubbleEvent
          : cloneEventMap(setting.bubbleEvent),
      showEventAxis: setting.showEventAxis,
      showEventBubbles: setting.showEventBubbles,
      showExperimentEventDetailFlat:
        setting.showExperimentEventDetailFlat ?? prev.showExperimentEventDetailFlat ?? false,
    }));
  }, []);

  // 最终变更函数
  const effectiveChangeSetting: (setting: IChartEventSetting) => void = isControlledMode
    ? (externalChangeSetting as (setting: IChartEventSetting) => void)
    : internalChangeSetting;

  return {
    eventSetting: effectiveEventSetting,
    changeEventSetting: effectiveChangeSetting,
  };
}
